/**
 * LeadQuickActions - Quick action buttons for leads (call, email, SMS)
 *
 * Provides one-click actions to contact leads directly.
 * Tracks contact activity when actions are taken.
 */

import { cn } from '@limo/shared'
import { Button } from '@/components/ui/button'
import { Phone, Mail, MessageSquare, ExternalLink } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

interface LeadQuickActionsProps {
  phone?: string | null
  email?: string | null
  onCallClick?: () => void
  onEmailClick?: () => void
  onSmsClick?: () => void
  compact?: boolean
  className?: string
}

export function LeadQuickActions({
  phone,
  email,
  onCallClick,
  onEmailClick,
  onSmsClick,
  compact = false,
  className,
}: LeadQuickActionsProps) {
  const hasPhone = !!phone
  const hasEmail = !!email

  const handleCall = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (hasPhone) {
      // Open tel: link
      window.open(`tel:${phone}`, '_self')
      onCallClick?.()
    }
  }

  const handleEmail = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (hasEmail) {
      // Open mailto: link
      window.open(`mailto:${email}`, '_self')
      onEmailClick?.()
    }
  }

  const handleSms = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (hasPhone) {
      // Open sms: link
      window.open(`sms:${phone}`, '_self')
      onSmsClick?.()
    }
  }

  if (compact) {
    return (
      <TooltipProvider>
        <div className={cn('flex items-center gap-1', className)}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={handleCall}
                disabled={!hasPhone}
                className={cn(
                  'p-1.5 rounded-md transition-colors',
                  hasPhone
                    ? 'hover:bg-green-100 text-green-600 hover:text-green-700'
                    : 'text-muted-foreground/40 cursor-not-allowed'
                )}
              >
                <Phone className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              {hasPhone ? `Call ${phone}` : 'No phone number'}
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={handleEmail}
                disabled={!hasEmail}
                className={cn(
                  'p-1.5 rounded-md transition-colors',
                  hasEmail
                    ? 'hover:bg-blue-100 text-blue-600 hover:text-blue-700'
                    : 'text-muted-foreground/40 cursor-not-allowed'
                )}
              >
                <Mail className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              {hasEmail ? `Email ${email}` : 'No email address'}
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={handleSms}
                disabled={!hasPhone}
                className={cn(
                  'p-1.5 rounded-md transition-colors',
                  hasPhone
                    ? 'hover:bg-purple-100 text-purple-600 hover:text-purple-700'
                    : 'text-muted-foreground/40 cursor-not-allowed'
                )}
              >
                <MessageSquare className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              {hasPhone ? `Text ${phone}` : 'No phone number'}
            </TooltipContent>
          </Tooltip>
        </div>
      </TooltipProvider>
    )
  }

  // Full view with labels
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <Button
        size="sm"
        variant="outline"
        onClick={handleCall}
        disabled={!hasPhone}
        className={cn(
          'gap-1.5',
          hasPhone && 'text-green-600 hover:text-green-700 hover:bg-green-50 border-green-200'
        )}
      >
        <Phone className="h-4 w-4" />
        Call
      </Button>

      <Button
        size="sm"
        variant="outline"
        onClick={handleEmail}
        disabled={!hasEmail}
        className={cn(
          'gap-1.5',
          hasEmail && 'text-blue-600 hover:text-blue-700 hover:bg-blue-50 border-blue-200'
        )}
      >
        <Mail className="h-4 w-4" />
        Email
      </Button>

      <Button
        size="sm"
        variant="outline"
        onClick={handleSms}
        disabled={!hasPhone}
        className={cn(
          'gap-1.5',
          hasPhone && 'text-purple-600 hover:text-purple-700 hover:bg-purple-50 border-purple-200'
        )}
      >
        <MessageSquare className="h-4 w-4" />
        Text
      </Button>
    </div>
  )
}

/**
 * LeadQuickActionsRow - Inline quick actions for table rows (shown on hover)
 */
interface LeadQuickActionsRowProps {
  phone?: string | null
  email?: string | null
  onCallClick?: () => void
  onEmailClick?: () => void
  className?: string
}

export function LeadQuickActionsRow({
  phone,
  email,
  onCallClick,
  onEmailClick,
  className,
}: LeadQuickActionsRowProps) {
  const hasPhone = !!phone
  const hasEmail = !!email

  const handleCall = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (hasPhone) {
      window.open(`tel:${phone}`, '_self')
      onCallClick?.()
    }
  }

  const handleEmail = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (hasEmail) {
      window.open(`mailto:${email}`, '_self')
      onEmailClick?.()
    }
  }

  return (
    <div className={cn('flex items-center gap-0.5', className)}>
      <button
        onClick={handleCall}
        disabled={!hasPhone}
        className={cn(
          'p-1 rounded transition-colors',
          hasPhone
            ? 'hover:bg-green-100 text-green-600'
            : 'text-muted-foreground/30 cursor-not-allowed'
        )}
        title={hasPhone ? `Call ${phone}` : 'No phone'}
      >
        <Phone className="h-3.5 w-3.5" />
      </button>

      <button
        onClick={handleEmail}
        disabled={!hasEmail}
        className={cn(
          'p-1 rounded transition-colors',
          hasEmail
            ? 'hover:bg-blue-100 text-blue-600'
            : 'text-muted-foreground/30 cursor-not-allowed'
        )}
        title={hasEmail ? `Email ${email}` : 'No email'}
      >
        <Mail className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}
